import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useIntro } from '../utils/hydration';
import { PlaybackIcon } from './AutoScroller';

const INDUSTRY_DURATION_MS = 10000;
// Тривалість перегону між роликами. Має збігатися з duration-700 у класах
// нижче — саме через неї ми знаємо, коли можна ставити старий буфер на паузу.
const FADE_MS = 700;

const Hero = () => {
  const [activeIndustryIndex, setActiveIndustryIndex] = useState(0);
  // Перший екран: у пререндереній сторінці він уже видимий до JavaScript,
  // і анімація появи лише погасила б його на секунду-дві.
  const intro = useIntro();

  // Два відеоелементи замість одного. Раніше ролик міняли через підміну src
  // на єдиному <video>: старий кадр зникав миттєво, новий з'являвся лише
  // через ~200 мс, і фон стрибав різкою склейкою. Тепер наступний ролик
  // тихо вантажиться у прихований буфер, і лише коли він справді готовий
  // грати — буфери переганяються один в одного через прозорість.
  const videoRefs = useRef([null, null]);
  const [visibleBuffer, setVisibleBuffer] = useState(0);
  const visibleBufferRef = useRef(0);
  // Покоління переходу: якщо клікнути кілька напрямків підряд, застарілі
  // обробники canplay не мають права нічого показувати.
  const swapRef = useRef(0);
  // Напрямок, під який ролик готували востаннє: інший — переганяємо буфери,
  // той самий (перший запуск, зняття паузи) — вантажимо на місці.
  const lastIndexRef = useRef(null);
  const intervalRef = useRef(null);
  const sectionRef = useRef(null);
  const inViewRef = useRef(true);

  const backgroundVideos = useMemo(() => [
    '/video/bg1.mp4',
    '/video/bg2.mp4',
    '/video/bg3.mp4',
    '/video/bg4.mp4',
    '/video/bg5.mp4',
    '/video/bg6.mp4',
  ], []);

  const posters = useMemo(() => [
    '/video/bg1.jpg',
    '/video/bg2.jpg',
    '/video/bg3.jpg',
    '/video/bg4.jpg',
    '/video/bg5.jpg',
    '/video/bg6.jpg',
  ], []);

  const industries = useMemo(() => [
    { icon: '01', label: 'Старт вашої справи «під ключ»' },
    { icon: '02', label: 'Ліцензування медичної практики' },
    { icon: '03', label: 'Захист інтелектуальної власності' },
    { icon: '04', label: 'Реєстрація та супровід в системі ЕСОЗ' },
    { icon: '05', label: 'Повний документальний супровід' },
    { icon: '06', label: 'Юридичний аутсорсинг' },
  ], []);

  // Чи вантажити відео взагалі. Це декоративний фон: на вузькому екрані його
  // майже не видно за текстом і затемненням, зате коштує ~0.5 МБ мобільного
  // трафіку і саме воно тягне LCP за поріг Google (виміряно 2.58 с на 4G).
  // На телефонах лишаємо постер — виглядає так само, відкривається вдвічі швидше.
  //
  // Перевірка живе всередині ефекту, який і так перезапускається кожні 10 секунд
  // разом зі зміною напрямку, тому поворот планшета підхоплюється сам.
  const shouldLoadVideo = useCallback(() => {
    if (!window.matchMedia('(min-width: 1024px)').matches) return false;
    const connection = navigator.connection;
    if (connection && (connection.saveData || /^(slow-)?2g$/.test(connection.effectiveType || ''))) {
      return false;
    }
    return true;
  }, []);

  /*
    Пауза. Фон і стрічка напрямків рухаються самі без кінця, тож їх має
    бути змога зупинити (WCAG 2.2.2): кнопка «Пауза» спиняє і відео, і зміну
    напрямків. Для тих, хто в системі попросив менше руху, показ стартує вже
    зупиненим — відео навіть не вантажиться, лишається постер.

    Стан живе в рефі, а useState лише перемальовує кнопку: ефекти нижче
    читають реф, і рішення «не вантажити відео» встигає застосуватися ще до
    першого завантаження, у тому самому коміті.
  */
  const [playing, setPlaying] = useState(true);
  const playingRef = useRef(true);
  const setPlayback = useCallback((next) => {
    playingRef.current = next;
    setPlaying(next);
  }, []);

  // Поки фокус клавіатури на перемикачах напрямків, показ не йде далі сам:
  // інакше вибраний пункт мінявся б просто під фокусом.
  const [hold, setHold] = useState(false);
  const holdRef = useRef(false);
  const setHoldState = useCallback((next) => {
    holdRef.current = next;
    setHold(next);
  }, []);

  // Номер циклу для смужки прогресу: щоразу, як таймер запускається
  // заново, смужка теж починає з нуля, а не доїжджає стару анімацію.
  const [cycle, setCycle] = useState(0);

  // Має стояти першим серед ефектів: наступні вже читають playingRef.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (query.matches) setPlayback(false);
    const onChange = () => setPlayback(!query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [setPlayback]);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    // Під автоматизацією ротацію не заводимо. Прогін пререндеру триває
    // близько десяти секунд — рівно період зміни ролика, тож знімок ловив
    // то перший напрямок, то другий, і HTML виходив щоразу інший. Для
    // живого відвідувача нічого не змінюється.
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;
    setCycle((c) => c + 1);
    intervalRef.current = setInterval(() => {
      setActiveIndustryIndex((i) => (i + 1) % industries.length);
    }, INDUSTRY_DURATION_MS);
  }, [industries.length, stopTimer]);

  // Ротація йде, лише коли hero на екрані, вкладка видима, показ не на паузі
  // й фокус не на перемикачах. Відео — за тими самими умовами, крім фокуса.
  const syncPlayback = useCallback(() => {
    const onScreen = inViewRef.current && !document.hidden;
    if (onScreen && playingRef.current && !holdRef.current) startTimer();
    else stopTimer();

    const shown = videoRefs.current[visibleBufferRef.current];
    if (onScreen && playingRef.current) {
      if (shown && shown.getAttribute('src')) {
        const p = shown.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    } else {
      videoRefs.current.forEach((v) => v && v.pause());
    }
  }, [startTimer, stopTimer]);

  useEffect(() => {
    syncPlayback();
  }, [playing, hold, syncPlayback]);

  useEffect(() => () => stopTimer(), [stopTimer]);

  // Зупиняємо ротацію і відео, коли hero поза екраном або вкладка прихована.
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      inViewRef.current = entry.isIntersecting;
      syncPlayback();
    }, { threshold: 0.1 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    document.addEventListener('visibilitychange', syncPlayback);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', syncPlayback);
    };
  }, [syncPlayback]);

  // Показуємо щойно підготовлений буфер і гасимо той, що пішов.
  const revealBuffer = useCallback((buffer, generation) => {
    visibleBufferRef.current = buffer;
    setVisibleBuffer(buffer);
    window.setTimeout(() => {
      // За час згасання могли перемкнути ще раз — тоді це вже не наша справа.
      if (generation !== swapRef.current) return;
      const hidden = videoRefs.current[buffer === 0 ? 1 : 0];
      if (hidden) hidden.pause();
    }, FADE_MS);
  }, []);

  // Готуємо наступний ролик у прихованому буфері й переганяємо кадр лише
  // тоді, коли він реально може грати.
  //
  // Ефект перезапускається і на зняття паузи. Тоді напрямок той самий, і
  // ролик вантажиться просто в буфер на екрані — переганяти нема з чого, як і
  // на першому запуску.
  useEffect(() => {
    const generation = ++swapRef.current;
    const buffers = videoRefs.current;
    if (!buffers[0] || !buffers[1]) return undefined;

    const poster = process.env.PUBLIC_URL + posters[activeIndustryIndex];
    const src = process.env.PUBLIC_URL + backgroundVideos[activeIndustryIndex];

    const inPlace = lastIndexRef.current === null || lastIndexRef.current === activeIndustryIndex;
    lastIndexRef.current = activeIndustryIndex;
    const showing = visibleBufferRef.current;
    const target = inPlace ? showing : (showing === 0 ? 1 : 0);
    const incoming = buffers[target];

    // React не серіалізує `muted` як HTML-атрибут (це property-only проп),
    // тому у пререндереному HTML його немає — а без нього браузер блокує
    // автовідтворення й показує лише постер.
    buffers.forEach((v) => {
      v.muted = true;
      v.setAttribute('muted', '');
    });

    incoming.poster = poster;

    // Телефон, режим економії або пауза: відео не вантажимо, а постери
    // перемикаємо тим самим плавним переходом — виходить слайдшоу. Буфер,
    // у якому лишився старий ролик, скидаємо: інакше замість постера там
    // стояв би останній кадр того ролика.
    if (!playingRef.current || !shouldLoadVideo()) {
      if (!inPlace) {
        if (incoming.hasAttribute('src')) {
          incoming.removeAttribute('src');
          incoming.load();
        }
        revealBuffer(target, generation);
      }
      return undefined;
    }

    if (incoming.getAttribute('src') !== src) {
      incoming.src = src;
      incoming.load();
    }

    let started = false;
    const start = () => {
      if (started || generation !== swapRef.current) return;
      started = true;
      window.clearTimeout(timer);
      incoming.removeEventListener('canplay', start);
      if (inViewRef.current && !document.hidden) {
        const playPromise = incoming.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      }
      if (!inPlace) revealBuffer(target, generation);
    };

    // Запасний хід: canplay може не прийти зовсім (помилка мережі, дивний
    // кодек). Краще показати наступний кадр із затримкою, ніж застрягнути
    // на попередньому назавжди.
    const timer = window.setTimeout(start, 1200);
    if (incoming.readyState >= 3) start();
    else incoming.addEventListener('canplay', start);

    return () => {
      window.clearTimeout(timer);
      incoming.removeEventListener('canplay', start);
    };
  }, [activeIndustryIndex, playing, backgroundVideos, posters, shouldLoadVideo, revealBuffer]);

  const handleIndustryClick = (i) => {
    if (i === activeIndustryIndex) return;
    setActiveIndustryIndex(i);
    // Відлік до наступного напрямку — заново від вибраного, але лише якщо
    // показ і так ішов: клік по напрямку не знімає паузу.
    syncPlayback();
  };

  // Фокус, що прийшов мишею, ротацію не тримає: клік по напрямку й раніше
  // лише перезапускав відлік. :focus-visible відрізняє клавіатуру від миші.
  const handleTabsFocus = (event) => {
    let keyboard = true;
    try {
      keyboard = event.target.matches(':focus-visible');
    } catch (error) {
      // Браузер без :focus-visible — вважаємо, що це клавіатура.
    }
    if (keyboard && !holdRef.current) setHoldState(true);
  };
  const handleTabsBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setHoldState(false);
  };

  const rotationStopped = !playing || hold;
  const playbackLabel = playing
    ? 'Пауза: зупинити фонове відео й зміну напрямків'
    : 'Відтворити: запустити фонове відео й зміну напрямків';

  const playbackButton = (className) => (
    <button
      type="button"
      onClick={() => setPlayback(!playing)}
      aria-label={playbackLabel}
      title={playbackLabel}
      className={`inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/40 backdrop-blur-sm text-white font-medium hover:bg-black/60 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40 ${className}`}
    >
      <PlaybackIcon paused={!playing} className="w-3.5 h-3.5 flex-shrink-0" />
      <span>{playing ? 'Пауза' : 'Відтворити'}</span>
    </button>
  );

  return (
    <motion.section
      id="home"
      ref={sectionRef}
      data-tone="dark"
      className="hero-shell flex items-start relative overflow-hidden"
      initial={intro({ opacity: 0 })}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <div className="absolute inset-0 w-full h-full">
        {/*
          Два накладені буфери. Постер стоїть лише на першому: він і є
          LCP-елементом сторінки, тож має бути в пререндереному HTML.
          Другий буфер отримує і постер, і src уже з JS.

          motion-safe: для тих, хто вимкнув анімації в системі, перехід
          зникає і кадр міняється одразу — без раптового руху на весь екран.

          autoplay немає навмисно: запускає відео лише JS (play()), і лише
          коли можна — не на паузі й не за prefers-reduced-motion. Атрибут
          обійшов би обидві перевірки. Фон декоративний, тож і для диктора
          його немає.
        */}
        {[0, 1].map((buffer) => (
          <video
            key={buffer}
            ref={(el) => {
              videoRefs.current[buffer] = el;
            }}
            aria-hidden="true"
            muted
            loop
            playsInline
            preload={buffer === 0 ? 'auto' : 'none'}
            poster={buffer === 0 ? process.env.PUBLIC_URL + posters[0] : undefined}
            className={`absolute inset-0 w-full h-full object-cover motion-safe:transition-opacity motion-safe:duration-700 motion-safe:ease-out ${
              visibleBuffer === buffer ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}

        <div className="absolute inset-0 bg-black/50 pointer-events-none"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/20"></div>
      </div>

      <div className="absolute inset-0 z-10 pointer-events-none">
        {/*
          Кутики стояли не на своїх місцях на обох краях: верхній при sm:top-10
          (40px) заходив під шапку заввишки 64px, а нижній при lg:bottom-16
          опинявся всередині стрічки напрямків і на телефоні збігався з
          індикатором. Тепер обидва тримаються поза зайнятими зонами.
        */}
        <div className="absolute top-24 left-4 w-8 h-8 border-l-2 border-t-2 border-white/30 sm:top-28 sm:left-8 sm:w-16 sm:h-16 lg:top-28 lg:left-16 lg:w-12 lg:h-12 xl:top-32 xl:left-20 xl:w-16 xl:h-16"></div>
        <div className="absolute bottom-32 right-4 w-8 h-8 border-r-2 border-b-2 border-white/30 sm:right-8 sm:w-16 sm:h-16 lg:bottom-40 lg:right-16 lg:w-12 lg:h-12 xl:right-20 xl:w-16 xl:h-16"></div>
      </div>

      {/*
        Висота вікна тут ділиться на три: шапка згори, стрічка напрямків знизу,
        текст посередині. Раніше цього поділу не було — контейнер центрував
        вміст через items-center, а поверх центрування блок ще й зсувався вниз
        на mt-24. На ноутбуці 1366×768 під текст лишалося 512px при потрібних
        ~590, тож стрічка накривала нижній рядок. Тепер зайняті зони вирахувані
        відступами, а зсув прибрано: центрування робить свою роботу саме.
      */}
      <div className="section-container z-20 relative w-full flex items-center hero-shell pt-24 pb-28 lg:pt-28 lg:pb-24">
        <motion.div
          className="w-full max-w-4xl lg:max-w-5xl xl:max-w-6xl"
          initial={intro({ opacity: 0, y: 30 })}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
        >
          <motion.h1 className="text-4xl xs:text-5xl sm:text-6xl md:text-6xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-black mb-4 sm:mb-5 lg:mb-4 xl:mb-5 leading-tight font-display relative text-left text-white">
            <motion.span
              className="block relative"
              initial={intro({ opacity: 0 })}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <span className="relative z-10 bg-gradient-to-r from-white via-teal-100 to-white bg-clip-text text-transparent drop-shadow-2xl">
                VISIA
              </span>
            </motion.span>{' '}

            <motion.span
              className="block text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-2xl xl:text-3xl 2xl:text-4xl font-light text-white/80 mt-3 sm:mt-4 lg:mt-3 xl:mt-4 tracking-[0.3em]"
              initial={intro({ opacity: 0 })}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <span className="text-white/60">MEDICAL</span>{' '}
              <span className="text-white/40 ml-3">GROUP</span>
            </motion.span>{' '}

            <motion.span
              className="block text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-light mt-4 sm:mt-5 lg:mt-4 xl:mt-5 max-w-3xl lg:max-w-4xl leading-tight text-left text-white font-display tracking-normal"
              initial={intro({ opacity: 0 })}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
            >
              Юридичний фундамент успіху вашого медичного бізнесу
            </motion.span>
          </motion.h1>

          <motion.p
            className="text-base xs:text-lg sm:text-lg md:text-xl lg:text-lg xl:text-xl font-light max-w-2xl lg:max-w-3xl leading-relaxed text-left text-white/90"
            initial={intro({ opacity: 0 })}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.8 }}
          >
            Ви фокусуєтесь на здоров'ї пацієнтів — ми дбаємо про юридичну безпеку вашої практики.
          </motion.p>

        </motion.div>
      </div>

      <motion.div
        className="block lg:hidden absolute bottom-4 left-4 right-4 z-30"
        initial={intro({ opacity: 0, y: 20 })}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8, duration: 0.8 }}
      >
        <div className="flex items-center justify-center space-x-2 bg-black/30 backdrop-blur-sm rounded-lg px-4 py-2">
          <div className="flex space-x-1 flex-shrink-0" aria-hidden="true">
            {industries.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                  index === activeIndustryIndex
                    ? 'bg-teal-400'
                    : index < activeIndustryIndex
                    ? 'bg-white/60'
                    : 'bg-white/20'
                }`}
              />
            ))}
          </div>
          {/* min-w-0: найдовший підпис («Реєстрація та супровід в системі
              ЕСОЗ») інакше розпирає плашку замість того, щоб перенестись. */}
          <div className="ml-3 min-w-0 text-white/80 text-xs sm:text-sm font-medium">
            {industries[activeIndustryIndex].label}
          </div>
          {playbackButton('flex-shrink-0 px-3 py-1.5 text-xs')}
        </div>
      </motion.div>

      <motion.div
        className="hidden lg:block absolute bottom-0 left-0 right-0 w-full z-30"
        initial={intro({ opacity: 0, y: 20 })}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8, duration: 0.8 }}
      >
        {/* Рядок над стрічкою прозорий для кліків: під ним лише тло. */}
        <div className="flex justify-end px-4 xl:px-6 pb-3 pointer-events-none">
          {playbackButton('pointer-events-auto px-3.5 py-2 text-xs')}
        </div>

        {/*
          Напрямки — кнопки-перемикачі: вибраний позначено aria-pressed,
          тож диктор чує, який із шести зараз на екрані, а не лише бачить це
          кольором. Раніше це були div з onClick, до яких клавіатура не
          діставалася зовсім.
        */}
        <div
          role="group"
          aria-label="Напрямки роботи"
          className="w-full flex flex-nowrap"
          onFocus={handleTabsFocus}
          onBlur={handleTabsBlur}
        >
          {industries.map((industry, index) => {
            const isActive = index === activeIndustryIndex;
            const isPast = index < activeIndustryIndex;
            return (
              <motion.button
                key={index}
                type="button"
                aria-pressed={isActive}
                className={`relative flex-1 min-w-0 px-3 py-2.5 xl:px-4 xl:py-3 text-left border-r border-t border-white/10 backdrop-blur-sm cursor-pointer transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-300 ${
                  isActive ? 'bg-white/10 border-white/20' : 'hover:bg-white/5'
                }`}
                onClick={() => handleIndustryClick(index)}
                whileHover={{ y: -2 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <span className="absolute top-0 left-0 right-0 h-0.5 bg-white/10 overflow-hidden" aria-hidden="true">
                  {isActive ? (
                    <span
                      key={`progress-${activeIndustryIndex}-${cycle}`}
                      className="block h-full bg-gray-300 origin-left hero-progress-fill"
                      style={{
                        animationDuration: `${INDUSTRY_DURATION_MS}ms`,
                        animationPlayState: rotationStopped ? 'paused' : undefined
                      }}
                    />
                  ) : (
                    <span
                      className="block h-full bg-gray-300"
                      style={{ width: isPast ? '100%' : '0%' }}
                    />
                  )}
                </span>

                <span className="block text-xs xl:text-sm mb-0.5 opacity-90 font-bold tracking-widest text-teal-400" aria-hidden="true">
                  {industry.icon}
                </span>

                <span
                  className={`block text-[0.7rem] xl:text-xs leading-tight transition-colors duration-300 ${
                    isActive ? 'text-white' : 'text-white/70'
                  }`}
                >
                  {industry.label}
                </span>
              </motion.button>
            );
          })}
        </div>
      </motion.div>
    </motion.section>
  );
};

export default Hero;
